const article = {
  slug: "views-but-no-showings",
  cluster: "seller",
  title: "Views but No Showings? What It Means and How to Fix It",
  description:
    "Views but no showings? It almost always means your price or lead photo is filtering buyers out. Here's how to confirm the cause and what to fix first.",
  audience: "Sellers & agents",
  funnelStage: "TOFU",
  datePublished: "2026-06-25",
  dateModified: "2026-06-25",
  readingTime: "6 min read",
  tldr: "Lots of online views but few or no showings almost always means one of two things is acting as a filter: your price or your first photo. Views prove buyers are finding the listing; no showings means something in the listing is talking them out of it before they call. Check your view-to-showing ratio, then fix the lead photo first (fast, free) and re-test price next.",
  blocks: [
    {
      type: "p",
      spans: [
        "If your listing is racking up online views but the showing calendar is empty, the diagnosis is usually narrow: something in the listing itself is filtering buyers out before they pick up the phone. Views prove that buyers are finding your home in search. The lack of showings tells you they looked, formed a quick opinion, and decided it wasn't worth their time. In almost every case the culprit is one of two things — the price or the first photo — because those are the only two things most buyers actually evaluate before scrolling on.",
      ],
    },
    {
      type: "callout",
      spans: [
        "Views = buyers found your listing. Showings = buyers were convinced enough to act. A gap between them is a filtering problem, not a visibility problem.",
      ],
    },
    {
      type: "h2",
      text: "What does 'views but no showings' actually mean?",
      id: "what-it-means",
    },
    {
      type: "p",
      spans: [
        "A view is a passive action — a buyer (or a curious neighbor) tapped your listing in a search feed. A showing is a deliberate one: someone decided your home is worth an hour of their Saturday. Getting lots of the first and almost none of the second means your listing is succeeding at the top of the funnel and failing immediately after. Buyers are reaching the listing, glancing at the photo and the price, and bouncing.",
      ],
    },
    {
      type: "p",
      spans: [
        "This is genuinely good news compared to the alternative. If you had no views, you'd have a marketing or exposure problem — wrong keywords, weak syndication, bad timing. Views-but-no-showings is a conversion problem inside the listing, which is far cheaper and faster to fix because you control every lever. You don't need more eyeballs. You need to stop losing the eyeballs you already have.",
      ],
    },
    {
      type: "h2",
      text: "How do I confirm it's a real problem? (the view-to-showing ratio)",
      id: "view-to-showing-ratio",
    },
    {
      type: "p",
      spans: [
        "Before you change anything, quantify the gap. Pull your view count from the portal (Zillow, Realtor.com, your MLS) and divide showings into it. There's no universal magic number — it varies by price band, market heat, and whether your views are mostly real buyers or mostly tire-kickers — but the shape of the answer is what matters:",
      ],
    },
    {
      type: "table",
      headers: ["Pattern", "What it suggests", "Where to look first"],
      rows: [
        [
          "Hundreds of views, near-zero showings",
          "A hard filter is rejecting buyers at a glance",
          "Price, then lead photo",
        ],
        [
          "Healthy views, steady saves, no showings",
          "Buyers are interested but hesitating on value",
          "Price vs. condition mismatch",
        ],
        [
          "Many views, very few saves AND no showings",
          "The listing fails to earn a second look",
          "Lead photo and the first 3 images",
        ],
        [
          "Low views and low showings",
          "Exposure/visibility problem, not a filter",
          "Marketing, syndication, search terms",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "Saves (or favorites) are the tiebreaker stat. A save is a buyer raising their hand: 'I might want this.' If you have lots of views but very few saves, your photos aren't earning a second look — that points to the image problem. If you have plenty of saves but still no showings, buyers like the home but something — usually price relative to what they see — is stopping them from committing. We break the timing side of this down further in ",
        {
          text: "days on market explained",
          href: "/resources/days-on-market-explained",
        },
        ".",
      ],
    },
    {
      type: "stat",
      value: "A save is a buyer raising their hand; a showing is them standing up",
      label:
        "Use the views-to-saves-to-showings ladder to find exactly which rung you're losing buyers on",
    },
    {
      type: "h2",
      text: "Is it the price or the photo? (how to tell)",
      id: "price-or-photo",
    },
    {
      type: "p",
      spans: [
        "These are the two filters, and they fail in different ways. Learn to read the signature of each.",
      ],
    },
    {
      type: "h3",
      text: "It's probably the lead photo if…",
      id: "its-the-photo",
    },
    {
      type: "ul",
      items: [
        ["Views are decent but saves are low — people see it and immediately move on"],
        [
          "The first image is dark, cluttered, shot in bad light, or a weird angle (a closet, a bathroom, a gravel driveway)",
        ],
        [
          "The lead photo is a stock map, an interior shot, or anything that isn't the home's best face",
        ],
        [
          "Comparable listings nearby have bright, wide, professional exterior or hero shots and yours doesn't",
        ],
      ],
    },
    {
      type: "h3",
      text: "It's probably the price if…",
      id: "its-the-price",
    },
    {
      type: "ul",
      items: [
        [
          "You get views AND saves but still no showings — buyers like it but won't commit at this number",
        ],
        ["Your price-per-square-foot is visibly above similar homes that are getting showings"],
        [
          "Showings dried up right after a price increase, or never started at a price set above the comps",
        ],
        ["Buyers' agents have told you (or your agent) it's 'priced ahead of the market'"],
      ],
    },
    {
      type: "callout",
      spans: [
        "Rule of thumb: low saves point to the photo. High saves but no showings point to the price. The photo loses buyers in the feed; the price loses them after they've already liked the home.",
      ],
    },
    {
      type: "h2",
      text: "What should I fix first — the photo or the price?",
      id: "what-to-fix-first",
    },
    {
      type: "p",
      spans: [
        "Fix the lead photo first. Always. It is free, you can do it in minutes, and it's reversible — so there's no reason not to test it before you touch your price. Dropping your price is a one-way door that costs you real money and signals weakness to the market; you only want to walk through it once you're certain the listing itself isn't the problem. Run the cheap experiment before the expensive one.",
      ],
    },
    {
      type: "ol",
      items: [
        [
          "Swap the lead photo to the single most appealing exterior or hero shot — bright, wide, straight-on, ideally in good light. If your best exterior shot is gloomy or the lawn looks rough, an AI touch-up (sunnier sky, greener lawn, decluttered frame) can lift it without a reshoot. Keep it honest: altering real elements like the sky, lawn, or clutter is a material change that generally must be disclosed, with the unaltered original kept available, under most MLS rules and laws like California's AB 723 — so label edited photos and check your local rules.",
        ],
        [
          "Re-order the first 3–5 images so the strongest, most aspirational shots lead. Most buyers never scroll past the first handful. See ",
          {
            text: "the best order for listing photos",
            href: "/resources/best-order-for-listing-photos",
          },
          ".",
        ],
        [
          "Tighten the headline and first line of the description so the value is obvious at a glance — see ",
          {
            text: "how to write a listing description that sells",
            href: "/resources/how-to-write-a-listing-description-that-sells",
          },
          ".",
        ],
        ["Give it a few days and re-check your view-to-save and save-to-showing ratios."],
        [
          "If saves climb but showings still don't — or if saves were already healthy to begin with — the filter is price. Now pull a fresh ",
          {
            text: "CMA",
            href: "/resources/what-is-a-cma",
          },
          " and decide whether you're ",
          {
            text: "overpriced",
            href: "/resources/is-my-house-overpriced",
          },
          " relative to homes that are actually getting showings.",
        ],
      ],
    },
    {
      type: "p",
      spans: [
        "The reason for this order is payoff per unit of effort: a photo swap can be done before lunch and tells you something within days, while a price change is slow, public, and expensive to reverse. Exhaust the free, fast levers first, then make the costly decision with better information.",
      ],
    },
    {
      type: "h2",
      text: "What if I fix both and showings still don't come?",
      id: "still-no-showings",
    },
    {
      type: "p",
      spans: [
        "If the lead photo is strong, the price is in line with homes that are getting traffic, and showings still aren't happening, look at the next layer of filters: a thin or off-putting description, photo set that's too small or unflattering, restrictive showing instructions (hard-to-schedule, limited windows, occupied-and-messy), or a remarks line that quietly scares buyers off. Occasionally the issue is that your 'views' were never real buyers in the first place — a spike from a social share or a neighbor's curiosity won't produce showings no matter what you do. If you've genuinely been live a while with no traction, it may be time to consider ",
        {
          text: "relisting",
          href: "/resources/how-to-relist-a-house",
        },
        " to reset your days-on-market and get a fresh first impression.",
      ],
    },
    {
      type: "p",
      spans: [
        "A faster path: run the whole listing through an objective diagnostic instead of guessing. A ",
        {
          text: "Listing Review",
          href: "/get-started",
        },
        " assesses your price position, listing appeal, and photo sequencing, rewrites the description, and flags exactly which filter is costing you showings — so you change the right thing once instead of trial-and-error over weeks.",
      ],
    },
    {
      type: "callout",
      spans: [
        "Views but no showings is almost never a 'get more traffic' problem. It's a 'stop filtering out the traffic you already have' problem — and the lead photo and price are the filters, in that order.",
      ],
    },
  ],
  faqs: [
    {
      question: "How many views should I get before I worry about no showings?",
      answer: [
        {
          type: "p",
          spans: [
            "There's no universal threshold, because views vary wildly by price band, market heat, and how many of those views are real buyers versus curious neighbors. The signal to watch isn't the raw view count — it's the ratio. If you have a few hundred views and effectively zero showings, something in the listing is filtering buyers out and it's worth acting on. Use saves as a tiebreaker: low saves point to the lead photo, healthy saves with no showings point to the price.",
          ],
        },
      ],
    },
    {
      question: "Does changing the lead photo really bring more showings?",
      answer: [
        {
          type: "p",
          spans: [
            "It can, because most buyers form a yes-or-no impression from the thumbnail and the first few images before they ever read the description. A dark, cluttered, or oddly-cropped lead photo gets the listing skipped in the feed; a bright, wide, aspirational hero shot earns the second look that turns into a save and then a showing. It's also the cheapest, fastest, fully reversible thing you can test, which is why it should be the first lever you pull before touching price.",
          ],
        },
      ],
    },
    {
      question: "Should I lower my price if I'm getting views but no showings?",
      answer: [
        {
          type: "p",
          spans: [
            "Not as your first move. A price drop is expensive, public, and hard to reverse, so you only want to do it once you've ruled out the listing itself. First swap the lead photo, re-order your first few images, and tighten the description, then re-check your ratios after a few days. If saves are healthy but showings still don't come, that's the strongest sign the price is the filter — and at that point you should pull a fresh CMA and price against the homes that are actually getting showings, not against your original number.",
          ],
        },
      ],
    },
    {
      question: "What's the difference between a view, a save, and a showing?",
      answer: [
        {
          type: "p",
          spans: [
            "A view is passive — someone tapped your listing in search. A save (or favorite) is a buyer signaling intent: 'I might want this.' A showing is a commitment: they've arranged to see it in person. Reading these three in sequence tells you exactly where you're losing people. Lots of views but few saves means the photos aren't earning a second look. Plenty of saves but no showings means buyers like the home but the price or terms are stopping them from committing.",
          ],
        },
      ],
    },
    {
      question: "Could my views just be fake or not real buyers?",
      answer: [
        {
          type: "p",
          spans: [
            "Sometimes, yes. A sudden spike in views from a social share, a price-change notification, or curious neighbors won't translate into showings no matter how good the listing is, because those viewers were never in the market. That's why the view-to-save-to-showing ladder matters more than the headline view number — real, motivated buyers tend to save listings they're seriously considering. If you have high views, near-zero saves, and no showings, treat the view count with skepticism and focus on whether the listing is actually reaching qualified buyers.",
          ],
        },
      ],
    },
  ],
  related: [
    "days-on-market-explained",
    "best-order-for-listing-photos",
    "how-to-write-a-listing-description-that-sells",
    "what-is-a-cma",
  ],
};

export default article;
